import { useEffect, useState } from "react";
import { authEnabled } from "../AuthGate";
import { SignedIn } from "@clerk/clerk-react";
import { buyWithPetals, myEntitlements, myPetals, type Product } from "../../sim/payments";
import { productPetalsPrice } from "../../sim/petals";
import { PetalsPrice } from "./PetalsCoin";

/**
 * BUY WITH PETALS — the shop's second buy control, beside Stripe checkout.
 *
 * The server does the spend (POST /api/shop/buy-with-petals debits the cloud save and grants the same
 * entitlement a Stripe purchase does), so this is display plus one request: the balance comes from the
 * server, the button is disabled with the shortfall when it is short, and the balance shown afterwards is
 * the one the server returned. A running game hears about the spend on PETALS_SPENT_CHANNEL (sim/payments).
 *
 * Signed-in only: the entitlement binds to an account, so a build without Clerk shows the price but no
 * button, and a signed-out visitor gets the fiat control's "Sign in to buy".
 */
export interface PetalsBuy {
  /** the signed-in player's balance; null while loading, when signed out, or if it could not be read */
  balance: number | null;
  busyId: number | null;
  /** products this visit has bought (or found already owned) with Petals */
  owned: ReadonlySet<number>;
  /**
   * Does the player already OWN this product? True for one bought this visit, and for a PLANT product whose plant
   * is among their entitlements (sim/payments myEntitlements) — the server refuses to sell that one twice (409
   * "owned"), so the shop shows "✓ Owned" instead of offering a Buy it would refuse. False while signed out.
   */
  owns: (p: Product) => boolean;
  buy: (p: Product) => void;
}

/** Balance + purchase state for one shop surface. `onError` receives a message to show the player. */
export function usePetalsBuy(onError: (msg: string | null) => void): PetalsBuy {
  const [balance, setBalance] = useState<number | null>(null);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [owned, setOwned] = useState<ReadonlySet<number>>(new Set());
  const [ownedPlants, setOwnedPlants] = useState<ReadonlySet<string>>(new Set());

  useEffect(() => {
    if (!authEnabled) return;
    let alive = true;
    myPetals().then((b) => { if (alive) setBalance(b); }).catch(() => { if (alive) setBalance(null); });
    return () => { alive = false; };
  }, []);
  // What they already own. Not gated on `authEnabled`: a no-auth build's entitlements follow the anonymous player id.
  // A signed-out visitor's 401 just leaves the set empty — every card offers its normal buttons.
  useEffect(() => {
    let alive = true;
    myEntitlements().then((e) => { if (alive) setOwnedPlants(new Set(e.plants)); }).catch(() => {});
    return () => { alive = false; };
  }, []);
  const owns = (p: Product) => owned.has(p.id) || (p.grant?.type === "plant" && ownedPlants.has(p.grant.refId));

  async function buy(p: Product) {
    onError(null); setBusyId(p.id);
    try {
      const r = await buyWithPetals(p.id);
      setBalance(r.petals);
      setOwned((s) => new Set(s).add(p.id));
    } catch (e) {
      onError(String((e as Error).message || e));
      myPetals().then(setBalance).catch(() => {});   // a 402 means our number was stale — re-read it
    } finally {
      setBusyId(null);
    }
  }

  return { balance, busyId, owned, owns, buy };
}

/** The Petals half of a price row: "or ✿ 2,062". Renders nothing for a product with no Petals price. */
export function PetalsPriceTag({ product, orClass }: { product: Product; orClass: string }) {
  const price = productPetalsPrice(product);
  if (price == null) return null;
  return (
    <>
      <span className={orClass}>or</span>
      <PetalsPrice petals={price} />
    </>
  );
}

/** The "Buy with Petals" button, disabled with the shortfall shown when the balance is short. */
export function PetalsBuyControl({ product, petals, large = false }: { product: Product; petals: PetalsBuy; large?: boolean }) {
  const price = productPetalsPrice(product);
  if (price == null || !authEnabled) return null;
  const cls = `site-btn${large ? "" : " sm"}`;
  const { balance, busyId, owned } = petals;
  let btn;
  if (owned.has(product.id)) {
    btn = <button className={cls} disabled>Owned</button>;
  } else if (balance != null && balance < price) {
    const short = price - balance;
    btn = (
      <button className={cls} disabled title={`You have ${balance.toLocaleString()} Petals`}>
        Need {short.toLocaleString()} more Petals
      </button>
    );
  } else {
    btn = (
      <button className={cls} disabled={balance == null || busyId === product.id} onClick={() => petals.buy(product)}>
        {busyId === product.id ? "…" : "Buy with Petals"}
      </button>
    );
  }
  return <SignedIn>{btn}</SignedIn>;
}
